import * as React from 'react';
import { useClickAway } from 'react-use';
import styled from 'styled-components';
import cl from 'classnames';

import { flexGap, innerBorder } from '@doltech/utils/lib/css-style';
import { IconSystemV2SClose } from '@doltech/icons/SystemV2/IconSystemV2SClose';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { shadows } from '@doltech/ui/lib/figma/shadows';
import { ResponsiveTypography } from '@doltech/ui/lib/figma/Typography/v3/ResponsiveTypography';
import { TextLink } from '@doltech/ui/lib/figma/TextLink/TextLink';
import { ButtonIconPrimaryGeneral } from '@doltech/ui/lib/figma/Button/ButtonIconPrimaryGeneral/ButtonIconPrimaryGeneral';

const POPOVER_WIDTH = 300;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  ${flexGap(16, 'column')}

  border-radius: 16px;

  &.pop-up {
    width: ${POPOVER_WIDTH}px;
    padding: 20px;
    background-color: ${colorsV2.white100};
    ${innerBorder(`1px solid ${colorsV2.gray20}`)}
    box-shadow: ${shadows.zIndex2};
    ${flexGap(12, 'column')}
  }

  &.bottom-sheet {
    padding: 16px 12px 20px;
    background-color: ${colorsV2.white100};
    box-shadow: ${shadows.topZIndex2};
    border-radius: 16px 16px 0px 0px;

    .title-section {
      display: flex;
      align-items: center;

      .title {
        flex: 1;
      }
    }
  }
`;

interface SectionDictationQuestionListProps {
  type?: 'default' | 'pop-up' | 'bottom-sheet';
  groupContent?: React.ReactNode;
  triggerElement?: HTMLElement;
  onClose?: () => void;
}

export const SectionDictationQuestionList = (props: SectionDictationQuestionListProps) => {
  const { type = 'default', groupContent, triggerElement, onClose } = props;
  const ref = React.useRef<HTMLDivElement>(null);

  useClickAway(ref, (event) => {
    const isTriggerNode =
      triggerElement?.isSameNode(event.target as HTMLElement) ||
      triggerElement?.contains(event.target as HTMLElement);
    if (!isTriggerNode) {
      onClose?.();
    }
  });

  switch (type) {
    case 'bottom-sheet':
      return (
        <Container ref={ref} className={cl('section-dictation-question-list', 'bottom-sheet')}>
          <div className="title-section">
            <ResponsiveTypography.Text className="title" variant="semi-bold/20-28" color="black100">
              Danh sách câu hỏi
            </ResponsiveTypography.Text>
            <ButtonIconPrimaryGeneral
              size="large"
              type="white"
              buttonStyle="transparent"
              icon={<IconSystemV2SClose />}
              onClick={onClose}
            />
          </div>
          {groupContent}
        </Container>
      );
    case 'pop-up':
      return (
        <Container ref={ref} className={cl('section-dictation-question-list', 'pop-up')}>
          <div className="title-section">
            <ResponsiveTypography.Text variant="semi-bold/20-28" color="black100">
              Danh sách câu hỏi
            </ResponsiveTypography.Text>
          </div>
          {groupContent}
        </Container>
      );
  }

  return (
    <Container className="section-dictation-question-list">
      <TextLink
        color="gray"
        size="extra-large"
        fontSize="semi-bold"
        text="Ẩn danh sách câu hỏi"
        iconLeft={<IconSystemV2SClose />}
        onClick={onClose}
      />
      {groupContent}
    </Container>
  );
};
